import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, Filter, MessageSquare, Phone, Mic, ChevronRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const MODE_LABEL = {
  text: "Text",
  passive_call: "Anruf (du Verkäufer)",
  active_call: "Anruf (KI Verkäufer)",
};

const ModeIcon = ({ mode }) => {
  if (mode === "active_call" || mode === "passive_call") return <Phone className="w-3.5 h-3.5" />;
  return <MessageSquare className="w-3.5 h-3.5" />;
};

const ScoreBadge = ({ score }) => {
  if (score === null || score === undefined) return <span className="text-xs text-novatus-text-3">—</span>;
  let color = "text-red-400";
  if (score >= 70) color = "text-novatus-green";
  else if (score >= 50) color = "text-amber-400";
  return <span className={`font-display font-black text-lg ${color}`} data-testid="session-score">{score}</span>;
};

const StatusPill = ({ status }) => {
  if (status === "completed") {
    return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-novatus-green/15 text-novatus-green border border-novatus-green/40">abgeschlossen</span>;
  }
  if (status === "active") {
    return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-300 border border-amber-400/30">aktiv</span>;
  }
  return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-novatus-card-2 border border-novatus text-novatus-text-3">{status || "—"}</span>;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (e) { return iso; }
};

const AdminSessions = () => {
  const [data, setData] = useState({ total: 0, items: [] });
  const [users, setUsers] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    user_id: "",
    scenario_id: "",
    mode: "",
    status: "completed",
    min_score: "",
    max_score: "",
  });
  const [searchQuery, setSearchQuery] = useState("");

  const load = useCallback(async (currentFilters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(currentFilters).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) params.append(k, v);
      });
      params.append("limit", "100");
      const { data: res } = await api.get(`/admin/sessions?${params.toString()}`);
      setData(res);
    } catch (e) {
      toast.error("Konnte Sessions nicht laden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load lookup data once
    (async () => {
      try {
        const [u, s] = await Promise.all([
          api.get("/admin/users"),
          api.get("/scenarios"),
        ]);
        setUsers(u.data || []);
        setScenarios(s.data || []);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  useEffect(() => {
    load(filters);
  }, [filters, load]);

  const visibleItems = data.items.filter((s) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      s.user_name?.toLowerCase().includes(q) ||
      s.user_email?.toLowerCase().includes(q) ||
      s.scenario_title?.toLowerCase().includes(q)
    );
  });

  const resetFilters = () => {
    setFilters({ user_id: "", scenario_id: "", mode: "", status: "completed", min_score: "", max_score: "" });
    setSearchQuery("");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <Link to="/team" className="text-sm text-novatus-text-3 hover:text-novatus-green inline-flex items-center gap-1 mb-4" data-testid="back-to-team-btn">
        <ArrowLeft className="w-4 h-4" /> Zurück zu Team
      </Link>

      <div className="animate-slide-up">
        <span className="label-eyebrow">Trainer · Session-Review</span>
        <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight text-white mt-2">
          Alle Trainings <span className="text-novatus-green">einsehen.</span>
        </h1>
        <p className="text-novatus-text-2 mt-3 max-w-2xl">Transkripte und Voice-Analytics jeder Session — gezielt nach Verkäufer, Szenario oder Score filtern.</p>
      </div>

      {/* Filter bar */}
      <div className="nv-card mt-6 p-4 sm:p-5 animate-slide-up delay-1" data-testid="filters-card">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-novatus-green" />
          <h3 className="font-display font-bold text-white">Filter</h3>
          <button onClick={resetFilters} className="ml-auto text-xs text-novatus-text-3 hover:text-novatus-green" data-testid="reset-filters-btn">Zurücksetzen</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <label className="label-eyebrow block mb-1">Suche</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-novatus-text-3" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Verkäufer oder Szenario…"
                className="nv-input w-full pl-8"
                data-testid="filter-search"
              />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Verkäufer</label>
            <select
              value={filters.user_id}
              onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
              className="nv-input w-full"
              data-testid="filter-user"
            >
              <option value="">Alle</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Szenario</label>
            <select
              value={filters.scenario_id}
              onChange={(e) => setFilters({ ...filters, scenario_id: e.target.value })}
              className="nv-input w-full"
              data-testid="filter-scenario"
            >
              <option value="">Alle</option>
              {scenarios.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Modus</label>
            <select
              value={filters.mode}
              onChange={(e) => setFilters({ ...filters, mode: e.target.value })}
              className="nv-input w-full"
              data-testid="filter-mode"
            >
              <option value="">Alle</option>
              <option value="text">Text</option>
              <option value="passive_call">Anruf (du Verkäufer)</option>
              <option value="active_call">Anruf (KI Verkäufer)</option>
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="nv-input w-full"
              data-testid="filter-status"
            >
              <option value="">Alle</option>
              <option value="completed">Abgeschlossen</option>
              <option value="active">Aktiv</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mt-3">
          <div>
            <label className="label-eyebrow block mb-1">Score min</label>
            <input
              type="number"
              min="0"
              max="100"
              value={filters.min_score}
              onChange={(e) => setFilters({ ...filters, min_score: e.target.value })}
              className="nv-input w-full"
              data-testid="filter-min-score"
              placeholder="0"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Score max</label>
            <input
              type="number"
              min="0"
              max="100"
              value={filters.max_score}
              onChange={(e) => setFilters({ ...filters, max_score: e.target.value })}
              className="nv-input w-full"
              data-testid="filter-max-score"
              placeholder="100"
            />
          </div>
          <div className="flex items-end col-span-2 sm:col-span-2 lg:col-span-4">
            <p className="text-xs text-novatus-text-3" data-testid="filter-result-count">
              {loading ? "Lade…" : `${visibleItems.length} von ${data.total} Sessions angezeigt`}
            </p>
          </div>
        </div>
      </div>

      {/* Sessions table */}
      <div className="nv-card mt-5 overflow-x-auto animate-slide-up delay-2" data-testid="sessions-table">
        {loading ? (
          <div className="p-12 text-center text-novatus-text-3"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : visibleItems.length === 0 ? (
          <div className="p-12 text-center">
            <Search className="w-8 h-8 text-novatus-text-3 mx-auto mb-3" />
            <p className="text-sm text-novatus-text-2">Keine Sessions gefunden für diese Filter.</p>
            <button onClick={resetFilters} className="mt-3 text-xs text-novatus-green hover:underline">Filter zurücksetzen</button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-novatus-card-2 border-b border-novatus">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Datum</th>
                <th className="px-4 py-3 label-eyebrow">Verkäufer</th>
                <th className="px-4 py-3 label-eyebrow">Szenario</th>
                <th className="px-4 py-3 label-eyebrow">Modus</th>
                <th className="px-4 py-3 label-eyebrow text-right">Nachrichten</th>
                <th className="px-4 py-3 label-eyebrow text-right">Score</th>
                <th className="px-4 py-3 label-eyebrow">Status</th>
                <th className="px-4 py-3 label-eyebrow text-right"> </th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((s) => (
                <tr key={s.id} className="border-b border-novatus hover:bg-white/[0.02]" data-testid={`session-row-${s.id}`}>
                  <td className="px-4 py-3 text-novatus-text-2 whitespace-nowrap font-mono text-xs">{formatDate(s.ended_at || s.started_at)}</td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-white">{s.user_name}</p>
                    <p className="text-xs text-novatus-text-3">{s.user_email}</p>
                  </td>
                  <td className="px-4 py-3 text-white">{s.scenario_title}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-novatus-card-2 border border-novatus text-novatus-text-2">
                      <ModeIcon mode={s.mode} /> {MODE_LABEL[s.mode] || "Text"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-white">{s.message_count}</td>
                  <td className="px-4 py-3 text-right"><ScoreBadge score={s.score} /></td>
                  <td className="px-4 py-3"><StatusPill status={s.status} /></td>
                  <td className="px-4 py-3 text-right">
                    {s.status === "completed" ? (
                      <Link
                        to={`/team/sessions/${s.id}`}
                        className="text-xs btn-ghost-green h-8 px-3 rounded-md inline-flex items-center gap-1"
                        data-testid={`view-session-${s.id}`}
                      >
                        Ansehen <ChevronRight className="w-3 h-3" />
                      </Link>
                    ) : (
                      <span className="text-xs text-novatus-text-3">läuft…</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminSessions;
